import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Mail,
  Lock,
  User,
  Building,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import mainlogo from "@/assets/mylistiq-logo-new.png";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useToast } from "@/components/ui/use-toast";
import { detectUserTimezone } from "@/utils/timezoneDetection";
import image from "@/assets/hero-properties.jpg";
import InactiveAccountDialog from "@/components/Inactiveaccountdialogbox";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const getNextPath = () => {
    const nextPath = new URLSearchParams(location.search).get("next");
    return nextPath && nextPath.startsWith("/") ? nextPath : "/dashboard";
  };

  // ─── Login State ───────────────────────────────────────
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrors, setLoginErrors] = useState<{
    email?: string;
    password?: string;
    terms?: string;
    general?: string;
  }>({});
  const [termsAcceptedLogin, setTermsAcceptedLogin] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  // ─── Signup State ──────────────────────────────────────
  const [signupData, setSignupData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    countryCode: "+1",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signupErrors, setSignupErrors] = useState<{ [key: string]: string }>({});
  const [signupLoading, setSignupLoading] = useState(false);

  const [showDeletedAccountModal, setShowDeletedAccountModal] = useState(false);
  const [deletedAccountMessage, setDeletedAccountMessage] = useState("");
  const [showInactiveAccountModal, setShowInactiveAccountModal] = useState(false);
  const [activeTab, setActiveTab] = useState("signup");

  // ─── Helpers ───────────────────────────────────────────
  const getLocalPhoneDigits = (value: string, countryCode = "+1") => {
    const trimmed = value.trim();
    let digits = value.replace(/\D/g, "");
    const countryDigits = countryCode.replace(/\D/g, "");

    if (
      countryDigits &&
      (trimmed.startsWith("+") || digits.length > 10) &&
      digits.startsWith(countryDigits)
    ) {
      digits = digits.slice(countryDigits.length);
    }

    return digits.slice(0, 10);
  };

  const formatPhoneNumber = (value: string, countryCode = "+1") => {
    const digits = getLocalPhoneDigits(value, countryCode);
    if (countryCode === "+1") {
      if (digits.length === 0) return "";
      if (digits.length < 4) return `(${digits}`;
      if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return digits;
  };

  const countryOptions = [
    // { label: "United States / Canada (+1)", value: "+1" },
    { label: "US / Canada (+1)", value: "+1" },
    { label: "United Kingdom (+44)", value: "+44" },
    { label: "Australia (+61)", value: "+61" },
    { label: "India (+91)", value: "+91" },
    { label: "Germany (+49)", value: "+49" },
    { label: "France (+33)", value: "+33" },
    { label: "Mexico (+52)", value: "+52" },
    { label: "Brazil (+55)", value: "+55" },
    { label: "Spain (+34)", value: "+34" },
    { label: "Netherlands (+31)", value: "+31" },
    { label: "South Africa (+27)", value: "+27" },
    { label: "Singapore (+65)", value: "+65" },
  ];

  const capitalizeWords = (value: string) => {
    return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const parseSignupErrorResponse = (response: any) => {
    if (!response) {
      return { general: "Signup failed. Please try again." };
    }

    if (response.errors && typeof response.errors === "object") {
      return response.errors;
    }

    if (response.email) {
      return {
        email: Array.isArray(response.email) ? response.email[0] : response.email,
      };
    }

    if (typeof response.message === "string" && response.message.trim()) {
      const message = response.message.trim();
      const separatorIndex = message.indexOf(":");

      if (separatorIndex > 0) {
        const rawField = message.slice(0, separatorIndex).trim();
        const fieldMessage = message.slice(separatorIndex + 1).trim();
        const fieldMap: Record<string, string> = {
          phone_number: "phone",
          first_name: "firstName",
          last_name: "lastName",
        };
        const mappedField = fieldMap[rawField] || rawField;

        if (fieldMessage) {
          return { [mappedField]: fieldMessage };
        }
      }

      return { general: message };
    }

    return { general: "Signup failed. Please try again." };
  };

  const getFriendlyLoginError = (err: any) => {
    const detail = err?.response?.data?.detail;
    const status = err?.response?.status;

    if (
      typeof detail === "string" &&
      detail.toLowerCase().includes("email is not registered")
    ) {
      return "Email is not registered.";
    }

    if (
      status === 401 ||
      (typeof detail === "string" &&
        detail.toLowerCase().includes("no active account found"))
    ) {
      return "Incorrect email or password. Please try again.";
    }

    if (status === 429) {
      return "Too many login attempts. Please wait a moment and try again.";
    }

    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }

    return "Login failed. Please try again.";
  };

  // ─── Login Validation ──────────────────────────────────
  const validateLoginField = (field: string, value: string) => {
    const errors = { ...loginErrors };
    if (field === "email") {
      if (!value.trim()) {
        errors.email = "Email is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errors.email = "Please enter a valid email address.";
      } else {
        delete errors.email;
      }
    }
    if (field === "password") {
      if (!value.trim()) {
        errors.password = "Password is required.";
      } else {
        delete errors.password;
      }
    }
    setLoginErrors(errors);
  };

  const validateLogin = () => {
    const errors: typeof loginErrors = {};
    if (!loginData.email.trim()) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginData.email))
      errors.email = "Please enter a valid email address.";
    if (!loginData.password.trim()) errors.password = "Password is required.";
    // if (!termsAcceptedLogin) {
    //   errors.terms = "You must accept the Terms of Service and Privacy Policy to continue.";
    // }
    setLoginErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ─── Signup Validation ─────────────────────────────────
  const validateSignupField = (field: string, value: string) => {
    const errors = { ...signupErrors };
    switch (field) {
      case "firstName":
        if (!value.trim()) errors.firstName = "First name is required.";
        else delete errors.firstName;
        break;
      case "lastName":
        if (!value.trim()) errors.lastName = "Last name is required.";
        else delete errors.lastName;
        break;
      case "email":
        if (!value.trim()) errors.email = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          errors.email = "Please enter a valid email address.";
        else delete errors.email;
        break;
      case "company":
        if (!value.trim()) errors.company = "Company name is required.";
        else delete errors.company;
        break;
      case "phone":
        const rawPhoneDigits = getLocalPhoneDigits(value, signupData.countryCode);
        if (!rawPhoneDigits) {
          errors.phone = "Phone number is required.";
        } else if (rawPhoneDigits.length !== 10) {
          errors.phone = "Please enter a 10-digit phone number after the country code.";
        } else {
          delete errors.phone;
        }
        break;
      case "password":
        if (!value.trim()) errors.password = "Password is required.";
        else if (value.length < 8)
          errors.password = "Password must be at least 8 characters long.";
        else delete errors.password;
        if (signupData.confirmPassword && value !== signupData.confirmPassword)
          errors.confirmPassword = "Passwords do not match.";
        else if (signupData.confirmPassword) delete errors.confirmPassword;
        break;
      case "confirmPassword":
        if (!value.trim()) errors.confirmPassword = "Please confirm your password.";
        else if (value !== signupData.password)
          errors.confirmPassword = "Passwords do not match.";
        else delete errors.confirmPassword;
        break;
    }
    setSignupErrors(errors);
  };

  const validateSignup = () => {
    const errors: { [key: string]: string } = {};

    if (!signupData.firstName.trim()) errors.firstName = "First name is required.";
    if (!signupData.lastName.trim()) errors.lastName = "Last name is required.";
    if (!signupData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!signupData.company.trim()) errors.company = "Company name is required.";

    const phoneDigits = getLocalPhoneDigits(signupData.phone, signupData.countryCode);
    if (!phoneDigits) {
      errors.phone = "Phone number is required.";
    } else if (phoneDigits.length !== 10) {
      errors.phone = "Please enter a 10-digit phone number after the country code.";
    }

    if (!signupData.password.trim()) {
      errors.password = "Password is required.";
    } else if (signupData.password.length < 8) {
      errors.password = "Password must be at least 8 characters long.";
    }

    if (!signupData.confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (signupData.password !== signupData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }

    // Updated terms validation
    if (!termsAccepted) {
      errors.terms = "You must accept the communications consent to continue.";
    }

    setSignupErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ─── Handlers ──────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLogin()) return;
    setLoginLoading(true);
    setLoginErrors({});
    try {
      const res = await axiosInstance.post(endpoints.login, loginData);
      if (res.data.access && res.data.refresh) {
        // ✅ IMPROVED: Prioritize user's stored timezone from profile
        // If profile has timezone set, use that; otherwise fall back to system detection
        const userTimezone = res.data.user?.timezone || detectUserTimezone();

        // Store in both sessionStorage + localStorage for compatibility
        // with existing components until full migration is complete.
        sessionStorage.setItem("access_token", res.data.access);
        sessionStorage.setItem("refresh_token", res.data.refresh);
        sessionStorage.setItem("user", JSON.stringify(res.data.user));
        sessionStorage.setItem("just_logged_in", "true");
        sessionStorage.setItem("has_plan", res.data.user.plan ? "true" : "false");
        sessionStorage.setItem("user_timezone", userTimezone);

        localStorage.setItem("access_token", res.data.access);
        localStorage.setItem("refresh_token", res.data.refresh);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        localStorage.setItem("just_logged_in", "true");
        localStorage.setItem("has_plan", res.data.user.plan ? "true" : "false");
        localStorage.setItem("user_timezone", userTimezone);

        toast({
          title: "Login successful!",
          description: "Welcome back to MyListIQ.",
        });
        navigate(getNextPath(), { replace: true });
      }
    } catch (err: any) {
      console.error("Login error:", err);
      const responseData = err.response?.data;

      if (responseData?.deleted_account) {
        setDeletedAccountMessage(
          responseData.detail ||
          "This email was previously linked to a deleted account. No previous data will be restored. Do you want to resubscribe?"
        );
        setShowDeletedAccountModal(true);
        setLoginErrors({});
        return;
      }

      if (responseData?.is_active === false || responseData?.is_active === "False") {
        setShowInactiveAccountModal(true);
        setLoginErrors({});
        return;
      }

      if (responseData?.errors) {
        setLoginErrors(responseData.errors);
      } else if (responseData?.detail) {
        const friendlyMessage = getFriendlyLoginError(err);
        setLoginErrors({ general: friendlyMessage });
      } else {
        const friendlyMessage = getFriendlyLoginError(err);
        setLoginErrors({ general: friendlyMessage });
        toast({
          title: "Login failed",
          description: friendlyMessage,
          variant: "destructive",
        });
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleConfirmResubscribe = async () => {
    if (!loginData.email.trim() || !loginData.password.trim()) {
      setLoginErrors({ general: "Please enter your email and password to resubscribe." });
      return;
    }
    setLoginLoading(true);
    setLoginErrors({});
    try {
      const res = await axiosInstance.post(endpoints.login, {
        ...loginData,
        reactivate: true,
      });
      if (res.data.access && res.data.refresh) {
        const userTimezone = res.data.user?.timezone || detectUserTimezone();

        sessionStorage.setItem("access_token", res.data.access);
        sessionStorage.setItem("refresh_token", res.data.refresh);
        sessionStorage.setItem("user", JSON.stringify(res.data.user));
        sessionStorage.setItem("just_logged_in", "true");
        sessionStorage.setItem("has_plan", res.data.user.plan ? "true" : "false");
        sessionStorage.setItem("user_timezone", userTimezone);

        localStorage.setItem("access_token", res.data.access);
        localStorage.setItem("refresh_token", res.data.refresh);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        localStorage.setItem("just_logged_in", "true");
        localStorage.setItem("has_plan", res.data.user.plan ? "true" : "false");
        localStorage.setItem("user_timezone", userTimezone);

        toast({
          title: "Account reactivated!",
          description: "Your account has been restored and you can continue using MyListIQ.",
        });
        navigate(getNextPath(), { replace: true });
      }
    } catch (err: any) {
      console.error("Resubscribe error:", err);
      const responseData = err.response?.data;
      if (responseData?.errors) {
        setLoginErrors(responseData.errors);
      } else if (responseData?.detail) {
        setLoginErrors({ general: responseData.detail });
      } else {
        setLoginErrors({ general: "Unable to resubscribe. Please try again." });
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSignup()) return;
    setSignupLoading(true);
    setSignupErrors({});
    try {
      // ✅ IMPROVED: Auto-detect timezone for new users
      // New users won't have timezone in profile yet, so we auto-detect it
      const autoDetectedTimezone = detectUserTimezone();

      const res = await axiosInstance.post(
        endpoints.signup,
        {
          first_name: signupData.firstName,
          last_name: signupData.lastName,
          email: signupData.email,
          company: signupData.company,
          phone_number: `${signupData.countryCode}${getLocalPhoneDigits(signupData.phone, signupData.countryCode)}`,
          password: signupData.password,
          timezone: autoDetectedTimezone, // Auto-detected timezone for new user
        },
        { withCredentials: false }
      );
      if (res.data.access && res.data.refresh) {
        // ✅ IMPROVED: Prioritize user's stored timezone from response
        // On signup, backend should return the timezone we just set (or user's preference if they had one)
        const userTimezone = res.data.user?.timezone || autoDetectedTimezone;

        sessionStorage.setItem("access_token", res.data.access);
        sessionStorage.setItem("refresh_token", res.data.refresh);
        sessionStorage.setItem("user", JSON.stringify(res.data.user));
        sessionStorage.setItem("just_logged_in", "true");
        sessionStorage.setItem("has_plan", res.data.user.plan ? "true" : "false");
        sessionStorage.setItem("user_timezone", userTimezone);

        localStorage.setItem("access_token", res.data.access);
        localStorage.setItem("refresh_token", res.data.refresh);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        localStorage.setItem("just_logged_in", "true");
        localStorage.setItem("has_plan", res.data.user.plan ? "true" : "false");
        localStorage.setItem("user_timezone", userTimezone);

        toast({
          title: "Account created successfully! 🎉",
          description: "Welcome to MyListIQ.",
        });
        navigate(getNextPath(), { replace: true });
      }
    } catch (err: any) {
      console.error("Signup error:", err);
      const response = err.response?.data;
      const parsedErrors = parseSignupErrorResponse(response);
      setSignupErrors(parsedErrors);

      if (parsedErrors.general) {
        toast({
          title: "Signup failed",
          description: parsedErrors.general,
          variant: "destructive",
        });
      } else if (!parsedErrors.phone && !parsedErrors.email) {
        toast({
          title: "Signup failed",
          description: "Please review the highlighted fields and try again.",
          variant: "destructive",
        });
      }
    } finally {
      setSignupLoading(false);
    }
  };

  // ─── JSX ───────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* LEFT: Hero Image Panel */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-black lg:h-screen lg:sticky lg:top-0">
        <img
          src={image}
          alt="Properties"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/60 z-10" />
        <div className="relative z-20 flex flex-col justify-between h-full p-12 text-white">
          <div>
            <Link to="/">
              <img
                src={mainlogo}
                alt="MyListIQ"
                className="h-20 object-contain drop-shadow-lg cursor-pointer"
              />
            </Link>
          </div>
          <div>
            <h1 className="text-4xl font-bold mb-4">
              Welcome Back to <br /> MyListIQ
            </h1>
            <p className="text-sm text-white/90 max-w-md">
              Your all-in-one property management solution. Continue managing your properties with
              ease and efficiency.
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT: Form Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-16 bg-background min-h-screen lg:min-h-0">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <Link to="/">
              <img src={mainlogo} alt="MyListIQ" className="h-10" />
            </Link>
          </div>

          <Tabs
            defaultValue="login"
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid grid-cols-2 mb-8 w-full h-12">
              <TabsTrigger value="login" className="text-base font-medium">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="signup" className="text-base font-medium">
                Sign Up
              </TabsTrigger>
            </TabsList>

            {/* LOGIN TAB */}
            <TabsContent value="login" className="space-y-6">
              <div className="space-y-2">
                <h2 className="text-3xl font-bold tracking-tight">Welcome Back!</h2>
                <p className="text-muted-foreground">
                  Enter your credentials to access your account
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                {loginErrors.general && (
                  <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                    {loginErrors.general}
                  </div>
                )}

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="name@company.com"
                      className={`pl-11 h-12 ${loginErrors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      value={loginData.email}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase();
                        setLoginData({ ...loginData, email: value });
                        validateLoginField("email", value);
                      }}
                      onBlur={(e) => validateLoginField("email", e.target.value)}
                    />
                  </div>
                  {loginErrors.email && <p className="text-sm text-red-500">{loginErrors.email}</p>}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:underline font-medium"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className={`pl-11 pr-11 h-12 ${loginErrors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                      value={loginData.password}
                      onChange={(e) => {
                        setLoginData({ ...loginData, password: e.target.value });
                        validateLoginField("password", e.target.value);
                      }}
                      onBlur={(e) => validateLoginField("password", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {loginErrors.password && (
                    <p className="text-sm text-red-500">{loginErrors.password}</p>
                  )}
                </div>


                <Button
                  type="submit"
                  className="w-full h-12 text-base font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  // disabled={loginLoading || !termsAcceptedLogin}
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* SIGNUP TAB */}
            <TabsContent value="signup" className="space-y-6">
              <div className="space-y-2">
                <h2 className="text-3xl font-bold tracking-tight">Create Account</h2>
                <p className="text-muted-foreground">Start managing your properties today</p>
              </div>

              <form onSubmit={handleSignup} className="space-y-4">
                {signupErrors.general && (
                  <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                    {signupErrors.general}
                  </div>
                )}

                {/* Names */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        id="firstName"
                        placeholder="John"
                        className={`pl-10 h-11 ${signupErrors.firstName ? "border-red-500" : ""}`}
                        value={signupData.firstName}
                        onChange={(e) => {
                          const value = capitalizeWords(e.target.value);
                          setSignupData({ ...signupData, firstName: value });
                          validateSignupField("firstName", value);
                        }}
                        onBlur={(e) => validateSignupField("firstName", e.target.value)}
                      />
                    </div>
                    {signupErrors.firstName && (
                      <p className="text-xs text-red-500">{signupErrors.firstName}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        id="lastName"
                        placeholder="Doe"
                        className={`pl-10 h-11 ${signupErrors.lastName ? "border-red-500" : ""}`}
                        value={signupData.lastName}
                        onChange={(e) => {
                          const value = capitalizeWords(e.target.value);
                          setSignupData({ ...signupData, lastName: value });
                          validateSignupField("lastName", value);
                        }}
                        onBlur={(e) => validateSignupField("lastName", e.target.value)}
                      />
                    </div>
                    {signupErrors.lastName && (
                      <p className="text-xs text-red-500">{signupErrors.lastName}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="name@company.com"
                      className={`pl-10 h-11 ${signupErrors.email ? "border-red-500" : ""}`}
                      value={signupData.email}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase();
                        setSignupData({ ...signupData, email: value });
                        validateSignupField("email", value);
                      }}
                      onBlur={(e) => validateSignupField("email", e.target.value)}
                    />
                  </div>
                  {signupErrors.email && <p className="text-xs text-red-500">{signupErrors.email}</p>}
                </div>

                {/* Company */}
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="company"
                      placeholder="ABC Properties LLC"
                      className={`pl-10 h-11 ${signupErrors.company ? "border-red-500" : ""}`}
                      value={signupData.company}
                      onChange={(e) => {
                        setSignupData({ ...signupData, company: e.target.value });
                        validateSignupField("company", e.target.value);
                      }}
                      onBlur={(e) => validateSignupField("company", e.target.value)}
                    />
                  </div>
                  {signupErrors.company && (
                    <p className="text-xs text-red-500">{signupErrors.company}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <div
                    className={`flex items-center h-11 rounded-md border ${signupErrors.phone ? "border-red-500" : "border-input"
                      } overflow-hidden`}
                  >
                    <select
                      className="h-full min-w-[120px] bg-transparent px-3 text-sm outline-none border-r border-input"
                      value={signupData.countryCode}
                      onChange={(e) => {
                        const nextCountryCode = e.target.value;
                        const formatted = formatPhoneNumber(signupData.phone, nextCountryCode);
                        setSignupData({
                          ...signupData,
                          countryCode: nextCountryCode,
                          phone: formatted,
                        });
                        validateSignupField("phone", formatted);
                      }}
                    >
                      {countryOptions.map((country) => (
                        <option key={country.value + country.label} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                    <Input
                      placeholder={signupData.countryCode === "+1" ? "(555) 123-4567" : "Phone number"}
                      className="flex-1 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
                      value={signupData.phone}
                      maxLength={signupData.countryCode === "+1" ? 14 : 10}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value, signupData.countryCode);
                        setSignupData({ ...signupData, phone: formatted });
                        validateSignupField("phone", formatted);
                      }}
                      onBlur={(e) => validateSignupField("phone", e.target.value)}
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Enter your phone number after the selected country code.
                  </p>
                  {signupErrors.phone && (
                    <p className="text-xs text-red-500">{signupErrors.phone}</p>
                  )}

                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="signup-password"
                      type={showSignupPassword ? "text" : "password"}
                      placeholder="Min. 8 characters"
                      className={`pl-10 pr-10 h-11 ${signupErrors.password ? "border-red-500" : ""}`}
                      value={signupData.password}
                      onChange={(e) => {
                        setSignupData({ ...signupData, password: e.target.value });
                        validateSignupField("password", e.target.value);
                      }}
                      onBlur={(e) => validateSignupField("password", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showSignupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {signupErrors.password && (
                    <p className="text-xs text-red-500">{signupErrors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter password"
                      className={`pl-10 pr-10 h-11 ${signupErrors.confirmPassword ? "border-red-500" : ""}`}
                      value={signupData.confirmPassword}
                      onChange={(e) => {
                        setSignupData({ ...signupData, confirmPassword: e.target.value });
                        validateSignupField("confirmPassword", e.target.value);
                      }}
                      onBlur={(e) => validateSignupField("confirmPassword", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {signupErrors.confirmPassword && (
                    <p className="text-xs text-red-500">{signupErrors.confirmPassword}</p>
                  )}
                </div>


                {/* COMMUNICATIONS CONSENT CHECKBOX */}
                <div className="flex items-start space-x-3 pt-4">
                  <Checkbox
                    id="terms-signup"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => {
                      setTermsAccepted(!!checked);
                      if (checked) {
                        setSignupErrors((prev) => {
                          const { terms, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                  />
                  <div className="grid gap-0.5 leading-none">
                    <label
                      htmlFor="terms-signup"
                      className="text-xs leading-relaxed cursor-pointer select-none"   // Changed to text-xs
                    >
                      By checking this box, I provide my consent for MyListIQ, LLC to send me text messages (SMS) to
                      the phone number provided and commercial electronic mail messages to the email address provided,
                      including promotional, transactional, and informational communications. I understand that my
                      consent is not a condition of purchase, and that message and data rates may apply for SMS. Reply{" "}
                      <strong className="font-semibold">STOP</strong> to opt out of SMS or{" "}
                      <strong className="font-semibold">HELP</strong> for assistance at any time. I have read and agree
                      to the{" "}
                      <a
                        href="/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href="/terms-and-conditions/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Terms &amp; Conditions
                      </a>
                      .{" "}



                    </label>
                    {signupErrors.terms && (
                      <p className="text-xs text-red-500 mt-1">{signupErrors.terms}</p>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={signupLoading || !termsAccepted}
                >
                  {signupLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create Account <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-center text-muted-foreground pt-2">
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline font-medium"
                    onClick={() => setActiveTab("login")}
                  >
                    Sign in
                  </button>
                </p>
              </form>
            </TabsContent>
          </Tabs>

          <Dialog open={showDeletedAccountModal} onOpenChange={setShowDeletedAccountModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Reactivate Deleted Account</DialogTitle>
                <DialogDescription>
                  {deletedAccountMessage}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowDeletedAccountModal(false)}
                  disabled={loginLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirmResubscribe}
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resubscribing...
                    </>
                  ) : (
                    "Resubscribe"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <InactiveAccountDialog
            open={showInactiveAccountModal}
            onOpenChange={setShowInactiveAccountModal}
            defaultEmail={loginData.email}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
